import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Copy, MousePointerClick } from "lucide-react";

const BLANK = { name: "", base_url: "", utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "" };

export default function CampaignsAdmin() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["admin-campaigns"], queryFn: async () => (await api.get("/admin/campaigns")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => (editId ? api.put(`/admin/campaigns/${editId}`, form) : api.post("/admin/campaigns", form)),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-campaigns"] }); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/campaigns/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-campaigns"] }); },
  });

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...BLANK, ...c }); setEditId(c.id); setOpen(true); };
  const trackedLink = (code) => `${API}/c/${code}`;
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied to clipboard"); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Campaigns &amp; Tracking Links</h1><p className="mt-1 text-sm text-subtle">Create UTM-tagged links and track clicks.</p></div>
        <Button onClick={openNew} className="bg-finblue hover:bg-finblueDark" data-testid="campaign-add"><Plus className="h-4 w-4" /> New Campaign</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Name</TableHead><TableHead>Tracked Link</TableHead><TableHead>Source / Medium</TableHead><TableHead>Clicks</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-subtle">No campaigns yet.</TableCell></TableRow>}
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                <TableCell className="font-medium text-ink">{c.name}</TableCell>
                <TableCell>
                  <button onClick={() => copy(trackedLink(c.code))} className="flex items-center gap-1.5 text-xs text-finblue hover:underline" data-testid={`campaign-copy-${c.id}`}>
                    <Copy className="h-3.5 w-3.5" /> /c/{c.code}
                  </button>
                </TableCell>
                <TableCell className="text-sm text-subtle">{[c.utm_source, c.utm_medium].filter(Boolean).join(" / ") || "—"}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1 font-heading text-lg font-bold text-navy"><MousePointerClick className="h-4 w-4 text-finblue" />{c.clicks || 0}</span></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`campaign-edit-${c.id}`}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del.mutate(c.id)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Campaign" : "New Campaign"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Campaign Name" full><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="campaign-form-name" /></F>
            <F label="Destination URL" full><Input value={form.base_url} onChange={(e) => set("base_url", e.target.value)} placeholder="https://broker.com/signup" data-testid="campaign-form-url" /></F>
            <F label="utm_source"><Input value={form.utm_source} onChange={(e) => set("utm_source", e.target.value)} placeholder="newsletter" /></F>
            <F label="utm_medium"><Input value={form.utm_medium} onChange={(e) => set("utm_medium", e.target.value)} placeholder="email" /></F>
            <F label="utm_campaign"><Input value={form.utm_campaign} onChange={(e) => set("utm_campaign", e.target.value)} placeholder="spring_promo" /></F>
            <F label="utm_content"><Input value={form.utm_content} onChange={(e) => set("utm_content", e.target.value)} placeholder="cta_button" /></F>
            <F label="utm_term" full><Input value={form.utm_term} onChange={(e) => set("utm_term", e.target.value)} placeholder="forex broker" /></F>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="campaign-form-save">{save.isPending ? "Saving..." : "Save Campaign"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
